import {
  AuthProps,
  OnboardingKycProps,
  OnboardingProps,
} from "../interface/api";
import Axios from "../utils/http-request";
import { getAccessToken } from "../utils/storage";

export const apiSignUp = async (values: AuthProps) => {
  try {
    const uri = `/auth/register`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const formData = JSON.stringify(values);

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details:
        e?.response?.data?.message ||
        e?.details ||
        e?.response?.data?.details[0],
    };
  }
};

export const apiSignIn = async (values: AuthProps) => {
  try {
    const uri = `/auth/login`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const formData = JSON.stringify(values);

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details:
        e?.response?.data?.message || e?.response?.data?.error || e?.message,
    };
  }
};

export const apiOnboarding = async (values: OnboardingProps) => {
  const { accessToken, tokenType } = await getAccessToken();

  try {
    const uri = `/auth/onboard`;
    const formData = new FormData();

    // Set up headers for the request
    const cfg = {
      headers: {
        Authorization: `${tokenType} ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    };

    const dob_value = values.dob ?? "";

    formData.append("name", values.name);
    formData.append("dob", dob_value);
    formData.append("gender", values.gender);
    formData.append("state_id", values.state_id);
    formData.append("local_government_id", values.local_government_id);
    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error("Error details:", e.response?.data);
    return {
      data: {},
      status: "error",
      details: e.response?.data?.message || "Server error",
    };
  }
};

export const apiOnboardingKyc = async (values: OnboardingKycProps) => {
  const { accessToken, tokenType } = await getAccessToken();

  try {
    const uri = `/auth/onboard`;
    const formData = new FormData();

    // Set up headers for the request
    const cfg = {
      headers: {
        Authorization: `${tokenType} ${accessToken}`,
        "Content-Type": "multipart/form-data",
      },
    };

    const dob_value = values.dob ?? "";

    formData.append("kyc", values.kyc);
    formData.append("name", values.name);
    formData.append("dob", dob_value);
    formData.append("gender", values.gender);
    formData.append("state_id", values.state_id);
    formData.append("local_government_id", values.local_government_id);
    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error("Error details:", e.response?.data);
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};

export const apiActivateAccount = async (values: {
  otp: string;
  email: string;
}) => {
  try {
    const uri = `/auth/activate`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const formData = JSON.stringify(values);

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};

export const apiResendActivateEmail = async (email: string) => {
  try {
    const uri = `/auth/resend`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const formData = JSON.stringify(email);

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};

export const apiGetOnboardDetails = async () => {
  const { accessToken, tokenType } = await getAccessToken();
  try {
    const uri = `/auth/onboard`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        Authorization: `${tokenType} ${accessToken}`,
        "Content-Type": "application/json",
      },
    };

    // Send the GET request
    const result = await Axios.get(uri, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};

export const apiSendForgotPasswordEmail = async (email: string) => {
  try {
    const uri = `/auth/password/forgot`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const data = { email };
    const formData = JSON.stringify(data);

    // Send the POST request
    const result = await Axios.post(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};

export const apiPasswordReset = async (values: {
  email: string;
  otp: string;
  password: string;
  confirm_password: string;
}) => {
  try {
    const uri = `/auth/password/reset`;

    // Get request configuration (assuming this function provides headers, etc.)
    const cfg = {
      headers: {
        "Content-Type": "application/json",
      },
    };
    const formData = JSON.stringify(values);

    // Send the POST request
    const result = await Axios.put(uri, formData, cfg);

    // Return the result
    return result;
  } catch (e: any) {
    console.error(e);

    // Return error response
    return {
      data: {},
      status: "error",
      details: e?.response?.data?.message || e?.details,
    };
  }
};
